/**
 * Direct deep-link screen for the Contact Acquisition Workbench.
 */
import { formatInteger } from "./format.js";
import {
  documentThroughputQueueStageUrl,
  fetchJson,
  fetchSavedViews
} from "./dashboard-lead-factory-fetch.js";
import { applyUrlSavedViews } from "./dashboard-saved-view-apply.js";
import {
  contactWorkCapacityUrl,
  contactWorkOperatorEventsUrl,
  contactWorkOperatorOutcomesUrl,
  contactWorkQueueUrl,
  contactWorkThroughputUrl,
  getContactWorkFilters,
  getLeadInventoryFilters,
  leadInventoryUrl
} from "./dashboard-saved-view-state.js";
import {
  leadFactoryContactLetterPacketsUrl,
  leadFactoryContactWorkCapacityUrl,
  leadFactoryContactWorkOperatorEventsUrl,
  leadFactoryContactWorkOperatorOutcomesUrl,
  leadFactoryContactWorkQueueUrl,
  leadFactoryContactWorkThroughputUrl,
  leadFactoryDocumentThroughputUrl,
  leadFactoryLeadGenerationQueueUrl,
  leadFactoryLeadGenerationWorkbenchUrl,
  leadFactoryLeadsUrl,
  leadFactoryStToDtStatusUrl
} from "./dashboard-urls.js";
import { leadGenerationQueueHtml, leadGenerationWorkbenchHtml } from "./dashboard-lead-generation.js";
import { documentThroughputHtml, stToDtStatusHtml } from "./dashboard-document-throughput.js";
import { documentThroughputQueuesHtml } from "./dashboard-document-throughput-queues.js";
import { contactLeadHtml } from "./dashboard-contact-prep.js";
import { contactWorkSavedViewControlsHtml } from "./dashboard-saved-views.js";
import { contactWorkLaneHtml } from "./dashboard-contact-lanes.js";
import { isReturnedMailRetryLetterTask } from "./dashboard-command-center.js";
import {
  contactWorkBulkAssignmentHtml,
  contactWorkCapacityHtml,
  contactWorkCompletionPressureHtml,
  contactWorkDailyPlanHtml,
  contactWorkOperatorAccountabilityHtml,
  contactWorkOperatorActionHistoryHtml,
  contactWorkOperatorOutcomeReadbackHtml,
  contactWorkOperatorSummaryHtml,
  contactWorkSummaryHtml,
  contactWorkThroughputHtml
} from "./dashboard-contact-work.js";
import type {
  LeadFactoryContactLetterPacketsResponse,
  LeadFactoryContactWorkCapacityResponse,
  LeadFactoryContactWorkOperatorEventsResponse,
  LeadFactoryContactWorkOperatorOutcomesResponse,
  LeadFactoryContactWorkThroughputResponse,
  LeadFactoryDocumentThroughput,
  LeadFactoryDocumentThroughputQueueResponse,
  LeadFactoryDocumentThroughputQueues,
  LeadFactoryLeadGenerationQueueResponse,
  LeadFactoryLeadGenerationWorkbenchResponse,
  LeadFactoryLeadsResponse,
  LeadFactoryStToDtStatus,
  LeadFactoryTasksResponse
} from "./dashboard-lead-factory-types.js";

export function directContactAcquisitionRequested(): boolean {
  const value = new URLSearchParams(window.location.search).get("lf_contact_acquisition_direct")?.trim().toLowerCase() ?? "";
  return value === "1" || value === "true";
}

export async function renderDirectContactAcquisitionWorkbench(
  mount: HTMLElement,
  bindActions: (statusElement: HTMLElement) => void
): Promise<void> {
  await applyUrlSavedViews();
  const contactWorkFilters = getContactWorkFilters();
  const leadInventoryFilters = getLeadInventoryFilters();
  const leadGenerationWorkbench = await fetchJson<LeadFactoryLeadGenerationWorkbenchResponse>(leadFactoryLeadGenerationWorkbenchUrl);
  const leadGenerationQueue = await fetchJson<LeadFactoryLeadGenerationQueueResponse>(leadFactoryLeadGenerationQueueUrl);
  const documentThroughput = await fetchJson<LeadFactoryDocumentThroughput>(leadFactoryDocumentThroughputUrl);
  const stToDtStatus = await fetchJson<LeadFactoryStToDtStatus>(leadFactoryStToDtStatusUrl);
  const documentThroughputQueues: LeadFactoryDocumentThroughputQueues = {
    classification: await fetchJson<LeadFactoryDocumentThroughputQueueResponse>(documentThroughputQueueStageUrl("classification")),
    extraction: await fetchJson<LeadFactoryDocumentThroughputQueueResponse>(documentThroughputQueueStageUrl("extraction")),
    ocr: await fetchJson<LeadFactoryDocumentThroughputQueueResponse>(documentThroughputQueueStageUrl("ocr")),
    review: await fetchJson<LeadFactoryDocumentThroughputQueueResponse>(documentThroughputQueueStageUrl("review"))
  };
  const contactWorkSavedViews = await fetchSavedViews("contact_work");
  const contactWorkQueue = await fetchJson<LeadFactoryTasksResponse>(contactWorkQueueUrl(leadFactoryContactWorkQueueUrl, contactWorkFilters));
  const contactWorkCapacity = await fetchJson<LeadFactoryContactWorkCapacityResponse>(contactWorkCapacityUrl(leadFactoryContactWorkCapacityUrl, contactWorkFilters));
  const contactWorkThroughput = await fetchJson<LeadFactoryContactWorkThroughputResponse>(contactWorkThroughputUrl(leadFactoryContactWorkThroughputUrl, contactWorkFilters));
  const contactWorkOperatorEvents = await fetchJson<LeadFactoryContactWorkOperatorEventsResponse>(contactWorkOperatorEventsUrl(leadFactoryContactWorkOperatorEventsUrl, contactWorkFilters));
  const contactWorkOperatorOutcomes = await fetchJson<LeadFactoryContactWorkOperatorOutcomesResponse>(contactWorkOperatorOutcomesUrl(leadFactoryContactWorkOperatorOutcomesUrl, contactWorkFilters));
  const contactLetterPackets = await fetchJson<LeadFactoryContactLetterPacketsResponse>(leadFactoryContactLetterPacketsUrl);
  const targetLeads = await fetchJson<LeadFactoryLeadsResponse>(
    leadInventoryUrl(leadFactoryLeadsUrl, { ...leadInventoryFilters, limit: Math.max(10, leadInventoryFilters.limit) })
  );
  mount.innerHTML = `
    <main class="dashboard-shell contact-acquisition-direct" data-contact-acquisition-direct>
      <section class="lead-command" aria-label="contact acquisition workbench">
        <div class="lead-command-header">
          <div>
            <p class="eyebrow">Lead Factory CRM</p>
            <h1>Contact Acquisition Workbench</h1>
            <p class="subtle">Lead-generation actions, document throughput, contact work, and acquisition intake for the focused lead.</p>
          </div>
          <a class="button secondary" href="/">Dashboard</a>
        </div>
        ${leadGenerationWorkbenchHtml(leadGenerationWorkbench)}
        ${leadGenerationQueueHtml(leadGenerationQueue)}
        ${stToDtStatusHtml(stToDtStatus)}
        ${documentThroughputHtml(documentThroughput)}
        ${documentThroughputQueuesHtml(documentThroughputQueues)}
      </section>
      <section class="lead-layout" aria-label="contact acquisition focused work">
        <div class="panel">
          <div class="panel-heading">
            <h2>Focused Lead Actions</h2>
            <span class="subtle">${formatInteger(targetLeads.leads.length)} loaded</span>
          </div>
          <div class="contact-list">
            ${targetLeads.leads.slice(0, 10).map(contactLeadHtml).join("") || `<p class="empty">No focused contact lead is loaded.</p>`}
          </div>
        </div>
        <div class="panel">
          <div class="panel-heading">
            <h2>Contact Work</h2>
            <span class="subtle">${formatInteger(contactWorkQueue.summary?.loaded ?? contactWorkQueue.tasks.length)} loaded · ${formatInteger(contactWorkQueue.summary?.ready ?? contactWorkQueue.tasks.length)} ready</span>
          </div>
          ${contactWorkSavedViewControlsHtml(contactWorkSavedViews.views, contactWorkFilters)}
          ${contactWorkSummaryHtml(contactWorkQueue.summary)}
          ${contactWorkCapacityHtml(contactWorkCapacity)}
          ${contactWorkCompletionPressureHtml(contactWorkCapacity, contactWorkThroughput)}
          ${contactWorkDailyPlanHtml(contactWorkCapacity, contactWorkThroughput, contactWorkFilters)}
          ${contactWorkOperatorAccountabilityHtml(contactWorkCapacity, contactWorkThroughput, contactWorkFilters)}
          ${contactWorkOperatorActionHistoryHtml(contactWorkOperatorEvents)}
          ${contactWorkOperatorOutcomeReadbackHtml(contactWorkOperatorOutcomes)}
          ${contactWorkThroughputHtml(contactWorkThroughput)}
          ${contactWorkOperatorSummaryHtml(contactWorkQueue.summary)}
          ${contactWorkBulkAssignmentHtml(contactWorkQueue.tasks, contactWorkCapacity)}
          <div class="contact-work-grid">
            ${contactWorkLaneHtml("Calls", contactWorkQueue.tasks.filter((task) => task.task_type === "call_owner"), contactLetterPackets.packets, contactWorkFilters.leadId)}
            ${contactWorkLaneHtml("Retry Letters", contactWorkQueue.tasks.filter(isReturnedMailRetryLetterTask), contactLetterPackets.packets, contactWorkFilters.leadId)}
            ${contactWorkLaneHtml("Letters", contactWorkQueue.tasks.filter((task) => task.task_type === "prepare_letter" && !isReturnedMailRetryLetterTask(task)), contactLetterPackets.packets, contactWorkFilters.leadId)}
            ${contactWorkLaneHtml("Skip Trace", contactWorkQueue.tasks.filter((task) => task.task_type === "skip_trace"), contactLetterPackets.packets, contactWorkFilters.leadId)}
            ${contactWorkLaneHtml("PI", contactWorkQueue.tasks.filter((task) => task.task_type === "pi_research"), contactLetterPackets.packets, contactWorkFilters.leadId)}
          </div>
        </div>
      </section>
      <aside id="county-detail" class="detail-drawer" aria-live="polite"></aside>
      <p id="action-status" class="action-status" role="status" aria-live="polite">Contact Acquisition Workbench loaded.</p>
    </main>
  `;
  const statusElement = document.querySelector<HTMLElement>("#action-status");
  if (!statusElement) throw new Error("Missing dashboard element #action-status");
  bindActions(statusElement);
}
